import { DacDocsFormResponse, DynaDocDetails, DynaFieldWithDetails, DynaFieldType, ParamGeneralDacPlacement, ParamGeneralType } from '@/types';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, ChangeDetectorRef, NgZone, OnDestroy } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomFieldProps,
  DateFieldProps,
  DynamicForm,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  NumberFieldProps,
  TextFieldProps,
  TextGroupsFieldProps,
} from '@/components/form-builder/form-builder.types';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BehaviorSubject, catchError, map, of, Observable, Subscription } from 'rxjs';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { TableGroupsComponent } from '@/components/shared/table-groups/table-groups.component';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { object } from '@/utils/object.utils';
import { DacTransformationService } from '../dac-transformation.service';
import { FileSelectEvent } from 'primeng/fileupload';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';
import { TasksStatus } from '@/types/dac-responses.types';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { DacGeneralParamsService } from '../../settings/dac-general-params/dac-general-params.service';

@Component({
  selector: 'sm-dac-document-form',
  standalone: true,
  imports: [CommonModule, TableModule, TranslateModule, FormBuilderComponent, SpinnerComponent, PreviewPdfComponent],
  templateUrl: './dac-document-form.component.html',
  styleUrls: ['./dac-document-form.component.scss'],
})
export class DacDocumentFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly dacRepository = inject(DacRepository);
  private readonly dacTransformationService = inject(DacTransformationService);
  private readonly fileService = inject(FileService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly dacGeneralParamsService = inject(DacGeneralParamsService);

  // Global (reusable) variables placed in this document, shown alongside its local fields.
  // Value store is shared across the whole DAC (see DacRepository.getObservableGeneralParamValue),
  // so a variable already answered on an earlier document renders here pre-filled and disabled.
  private globalPlacements: ParamGeneralDacPlacement[] = [];

  @Input() dacId?: number;

  @Input() document: DynaDocDetails = {
    id: 0,
    version: 0,
    name: '',
    templateFileName: '',
    guideFileName: '',
    dynaDocOrder: 0,
    status: TasksStatus.BROUILLON,
    dynaFields: [],
    isStatic: false,
    docs: {
      id: 0,
      name: '',
      alfresco_id: '',
      pathFolder: '',
      fileSize: 0,
      alfrescoName: '',
      contentType: '',
      fileExtension: '',
    },
  };

  @Input() validationValues: DacDocsFormResponse[] = [];

  private staticFileSub?: Subscription;

  formObject: DynamicForm = {
    title: '',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [],
      },
    ],
  };

  loading$ = new BehaviorSubject<boolean>(false);

  pdfUrl = new BehaviorSubject<string>('');

  ngOnInit(): void {
    if (this.document.isStatic) {
      this.buildStaticForm();
      // On-demand loading: we don't call initializeStaticPreview() here to avoid API storm on init
    } else this.loadGlobalPlacementsThenBuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['document'] && !changes['document'].firstChange) {
      if (this.document.isStatic) {
        this.buildStaticForm();
      } else this.loadGlobalPlacementsThenBuild();
    }
  }

  private loadGlobalPlacementsThenBuild(): void {
    if (!this.document.id || !this.document.version) {
      this.globalPlacements = [];
      this.buildDynamicForm();
      // This data can arrive asynchronously after the parent stepper's initial (OnPush) check
      // already ran, so without an explicit markForCheck() here the rebuilt form silently never
      // reaches the screen even though this.formObject is already correct.
      this.cd.markForCheck();
      return;
    }

    this.dacGeneralParamsService
      .getPlacementsByDynaDocs(this.document.id, this.document.version)
      .pipe(catchError((): Observable<ParamGeneralDacPlacement[]> => of([])))
      .subscribe((placements: ParamGeneralDacPlacement[]) => {
        this.globalPlacements = placements;
        this.buildDynamicForm();
        // See comment above - without this, an OnPush ancestor can skip re-checking this
        // component after the async HTTP response updates the form, leaving the DOM stuck on
        // the pre-fetch render ("Aucun champ disponible") even though the data arrived fine.
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.pdfUrl.getValue()) {
      URL.revokeObjectURL(this.pdfUrl.getValue());
    }
    this.staticFileSub?.unsubscribe();
  }

  private buildField(dynaField: DynaFieldWithDetails) {
    const variant = this.dacTransformationService.getFieldVariant(dynaField.type);
    // Use a discriminated union on the return value
    switch (dynaField.type) {
      case DynaFieldType.TEXT: {
        const props: TextFieldProps = {
          placeholder: dynaField.label,
          value: this.dacRepository.getObservableFieldValue(dynaField.dynaFieldsId),
          onChange: (value: string) => {
            this.dacRepository.updateFieldValue(dynaField.dynaFieldsId, value);
          },
        };
        return { variant, props };
      }

      case DynaFieldType.INTEGER: {
        const props: NumberFieldProps = {
          placeholder: dynaField.label,
          value: this.dacRepository.getObservableFieldValue(dynaField.dynaFieldsId),
          onChange: (value: number) => {
            this.dacRepository.updateFieldValue(dynaField.dynaFieldsId, value);
          },
        };
        return { variant, props };
      }

      case DynaFieldType.DATE: {
        const props: DateFieldProps = {
          view: 'date',
          format: 'dd/mm/yy',
          value: this.dacRepository.getObservableFieldValue(dynaField.dynaFieldsId).pipe(map(v => (v ? new Date(v) : null))),
          onChange: (d: Date) => this.dacRepository.updateFieldValue(dynaField.dynaFieldsId, d),
        };
        return { variant, props };
      }

      case DynaFieldType.DATETIME: {
        const props: DateFieldProps = {
          view: 'date',
          format: 'dd/mm/yy',
          showTime: true,
          value: this.dacRepository.getObservableFieldValue(dynaField.dynaFieldsId).pipe(map(v => (v ? new Date(v) : null))),
          onChange: (d: Date) => this.dacRepository.updateFieldValue(dynaField.dynaFieldsId, d),
        };
        return { variant, props };
      }

      case DynaFieldType.SYSTEM: {
        const props: TextFieldProps = {
          placeholder: dynaField.label,
          value: this.dacRepository.getObservableFieldValue(dynaField.dynaFieldsId),
          onChange: () => {
            //this is a system field, no need to update
          },
        };
        return { variant, props };
      }

      case DynaFieldType.LIST: {
        const props: TextGroupsFieldProps = {
          singular: 'Ligne',
          value: this.dacRepository.getObservableFieldDetails(dynaField.dynaFieldsId).pipe(map(details => details.map(detail => detail.fieldValue))),
          onChange: (values: string[]) => this.dacTransformationService.onListChange(values, dynaField),
        };
        return { variant, props };
      }

      case DynaFieldType.TABLE: {
        const structure = this.dacTransformationService.getTableStructure(dynaField);

        const props: CustomFieldProps = {
          component: TableGroupsComponent,
          injectKeys: ['singular', 'value', 'onChange', 'structure'],
          singular: 'Ligne',
          structure,
          value: this.dacRepository.getObservableFieldDetails(dynaField.dynaFieldsId).pipe(
            map(details => {
              const flat = details.map(d => d.fieldValue);
              const cols = Object.keys(structure);
              return object.toRows(flat, cols);
            })
          ),
          onChange: (rows: Record<string, any>[]) => {
            this.dacTransformationService.onTableChange(rows, structure, dynaField);
          },
        };
        return { variant, props };
      }
      default: {
        const props: Record<string, unknown> = {};
        return { variant, props };
      }
    }
  }

  private getGlobalFieldVariant(type?: ParamGeneralType): FieldVariant {
    switch (type) {
      case ParamGeneralType.INTEGER:
      case ParamGeneralType.DECIMAL:
        return FieldVariant.NUMBER;
      case ParamGeneralType.DATE:
      case ParamGeneralType.DATETIME:
        return FieldVariant.DATE;
      default:
        return FieldVariant.TEXT;
    }
  }

  private buildGlobalField(placement: ParamGeneralDacPlacement) {
    const variant = this.getGlobalFieldVariant(placement.paramGeneralType);
    const value$ = this.dacRepository.getObservableGeneralParamValue(placement.paramGeneralDacId);
    const isSystem = placement.paramGeneralType === ParamGeneralType.SYSTEM;
    // A shared variable placed in several sections is only editable from its first section
    // (lowest document order within the document type); other sections just display the same
    // already-shared value (param_general_dac_values stores one row per variable+DAC, not per
    // section) read-only.
    const isFirstSection = placement.isFirstSection !== false;
    // Already answered on another document in this DAC -> shown here pre-filled, not re-asked.
    const alreadyFilled$ = value$.pipe(map(v => v !== undefined && v !== null && v !== ''));

    const onChange = (value: unknown) => this.dacRepository.updateGeneralParamValue(placement.paramGeneralDacId, value);

    let props: TextFieldProps | NumberFieldProps | DateFieldProps;
    if (variant === FieldVariant.DATE) {
      props = {
        view: 'date',
        format: 'dd/mm/yy',
        value: value$.pipe(map(v => (v ? new Date(v as string) : null))),
        onChange: (d: Date) => onChange(d),
      } as DateFieldProps;
    } else if (variant === FieldVariant.NUMBER) {
      props = {
        placeholder: placement.paramGeneralLabel,
        value: value$,
        onChange: (v: number) => onChange(v),
      } as NumberFieldProps;
    } else {
      props = {
        placeholder: placement.paramGeneralLabel,
        value: value$,
        onChange: (v: string) => onChange(v),
      } as TextFieldProps;
    }

    return {
      label: placement.paramGeneralLabel,
      variant,
      isRequired: false,
      description: placement.paramGeneralDescription,
      isDisabled: isSystem || !isFirstSection ? of(true) : alreadyFilled$,
      props,
    };
  }

  // Was `document.dynaFields.length === 0` - counted only local fields, so a document with
  // shared variables but zero local fields always showed "no fields available", regardless of
  // whether those shared variables actually loaded. Now reflects what's really on screen.
  protected get hasNoFields(): boolean {
    return this.document.dynaFields.length === 0 && this.globalPlacements.length === 0;
  }

  private buildDynamicForm() {
    this.formObject.title = this.document.name;

    const gridItems: { fields: any[] }[] = [];
    let currentItem: { fields: any[] } | null = null;

    const mustSplit = (type?: DynaFieldType) => type === DynaFieldType.TABLE || type === DynaFieldType.LIST;

    // One ordered list per section: local fields (dynaFieldsOrder) and global/shared
    // variables placed here (placement.ordre) share the same position space, sorted
    // together. Order set on the section's field-list screen is what's respected here.
    type MergedEntry = {
      order: number;
      type: DynaFieldType | 'GLOBAL';
      render: () => { label?: string; variant: FieldVariant; isRequired: boolean; description?: string; isDisabled: any; props: any };
    };

    const localEntries: MergedEntry[] = this.document.dynaFields.map((field, idx) => ({
      order: field.dynaFieldsOrder ?? idx + 1,
      type: field.type,
      render: () => {
        const built = this.buildField(field);
        return {
          label: field.label,
          variant: built.variant,
          isRequired: false,
          description: field.description,
          isDisabled: of(field.type == DynaFieldType.SYSTEM),
          props: { ...built.props },
        };
      },
    }));

    const globalEntries: MergedEntry[] = this.globalPlacements.map((placement, idx) => ({
      order: placement.ordre ?? this.document.dynaFields.length + idx + 1,
      type: 'GLOBAL',
      render: () => this.buildGlobalField(placement),
    }));

    const merged = [...localEntries, ...globalEntries].sort((a, b) => a.order - b.order);

    merged.forEach((entry, idx) => {
      const newField = entry.render();

      if (!currentItem) {
        currentItem = { fields: [newField] };
        gridItems.push(currentItem);
        return;
      }

      const prevType = merged[idx - 1].type;
      const needNewGroup = mustSplit(entry.type as DynaFieldType) || mustSplit(prevType as DynaFieldType) || currentItem.fields.length >= 3;

      if (needNewGroup) {
        currentItem = { fields: [newField] };
        gridItems.push(currentItem);
      } else {
        currentItem.fields.push(newField);
      }
    });

    this.formObject.grids[0].gridItems = gridItems;
  }

  private buildStaticForm() {
    this.formObject.title = this.document.name;
    this.formObject.orientation = 'vertical';

    const existingFiles = this.document.docs?.alfresco_id
      ? ([
          {
            alfresco_id: this.document.docs.alfresco_id,
            filename: this.document.docs.alfrescoName,
            extension: this.document.docs.fileExtension,
            size: this.document.docs.fileSize,
          },
        ] as FileRepresentation[])
      : [];

    this.formObject.grids[0].gridItems = [
      {
        fields: [
          {
            label: 'Document',
            variant: FieldVariant.FILE,
            isRequired: false,
            props: {
              mode: 'basic',
              accept: 'image/*, application/pdf',
              multiple: false,
              value: of([]),
              files: of(existingFiles),
              openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.DAC),
              onFileDownLoad: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.DAC),
              onFileSelect: (event: FileSelectEvent) => {
                const file = event.currentFiles[0];
                const url = URL.createObjectURL(file);
                this.pdfUrl.next(url);
                this.dacRepository.updateStaticFile(this.document.id, file);
              },
              onFileRemoved: () => {
                this.dacRepository.updateStaticFile(this.document.id, null);
                this.pdfUrl.next('');
              },
            } satisfies FileFieldProps,
          },
        ],
      },
    ];
  }

  initializeStaticPreview() {
    this.staticFileSub = this.dacRepository.getObservableStaticFile(this.document.id).subscribe(file => {
      if (file) {
        const url = URL.createObjectURL(file);
        this.zone.run(() => {
          this.pdfUrl.next(url);
          this.cd.detectChanges();
        });
      } else {
        this.zone.run(() => {
          this.pdfUrl.next('');
          this.cd.detectChanges();
        });
      }
    });
  }
}
